"use client";

import * as React from "react";
import { ArrowLeft, X, Link as LinkIcon, CornerDownLeft } from "lucide-react";
import { DialogHeader } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { UploadableSource, UploadableURL, isURLSource } from "../../types";
import { cn } from "@/lib/utils";

function isValidUrl(value: string): boolean {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

interface AddLinkDialogProps {
  onBack: () => void;
  onClose: () => void;
  selectedSources: UploadableSource[];
  setSelectedSources: React.Dispatch<React.SetStateAction<UploadableSource[]>>;
}

export function AddLinkDialogContent({
  onBack,
  onClose,
  selectedSources,
  setSelectedSources,
}: AddLinkDialogProps) {
  const [input, setInput] = React.useState("");
  const [hasError, setHasError] = React.useState(false);

  const [selectedLinks, setSelectedLinks] = React.useState([]);

  // const selectedLinks = selectedSources.filter(isURLSource) as UploadableURL[];

  const addLink = (raw: string) => {
    const value = raw.trim();
    if (!value) return;

    if (!isValidUrl(value) || selectedLinks.some((l) => l.url === value)) {
      setHasError(true);
      return;
    }

    setSelectedLinks((prev) => [...prev, { url: value }]);
    setInput("");
    setHasError(false);
  };

  const handleAdd = () => {
    const value = input.trim();
    if (!value) return;

    // Check if URL is valid and not a duplicate
    if (!isValidUrl(value) || selectedLinks.some((l) => l.url === value)) {
      setHasError(true);
      return;
    }

    // If valid, add the link
    addLink(input);
  };

  const handleRemove = (link: { url: string }) => {
    setSelectedLinks((prev) => prev.filter((p) => p.url !== link.url));
  };

  const handleConfirm = () => {
    setSelectedSources((prev) => [...prev, ...selectedLinks]);
  };

  return (
    <>
      <DialogHeader className="relative flex flex-row items-center justify-between pb-4">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="h-8 w-8 hover:bg-theme-1"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-xl font-semibold">Add Links</h2>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="h-8 w-8 hover:bg-theme-1 absolute right-0 top-0"
        >
          <X className="h-4 w-4" />
        </Button>
      </DialogHeader>

      <div className="space-y-4 -mt-4">
        <p className="text-theme-1">
          Paste in URLs to use as sources to study with.
        </p>

        <div
          className={cn(
            "flex items-start gap-2 rounded-md border px-3 py-3",
            hasError && "border-red-500"
          )}
        >
          <LinkIcon className="h-4 w-4 text-theme-1 shrink-0 mt-0.5" />

          <textarea
            placeholder="Paste link"
            value={input}
            rows={4}
            onChange={(e) => {
              setInput(e.target.value);
              setHasError(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                addLink(input);
              }
            }}
            className={cn(
              "flex-1 w-full bg-transparent text-sm outline-none placeholder:text-theme-1 resize-none",
              hasError && "text-red-600 placeholder:text-red-400"
            )}
          />

          {/* Enter hint */}
          {input && (
            <div className="flex items-center gap-1 text-[10px] text-theme-1 mt-0.5">
              <CornerDownLeft className="h-3 w-3" />
              Enter
            </div>
          )}
        </div>

        {selectedLinks.length > 0 && (
          <div className="space-y-2 max-h-60 overflow-y-auto">
            <h4 className="text-sm font-medium">Selected links:</h4>
            {selectedLinks.map((link, index) => (
              <div
                key={`link-${index}`}
                className="flex items-center gap-2 bg-theme-1 rounded-md px-3 py-2 min-w-0"
              >
                <div className="text-xs px-2 py-0.5 rounded bg-theme-2 text-theme-0 shrink-0">
                  LINK
                </div>
                <p className="flex-1 truncate text-sm min-w-0 w-full">
                  {link.url}
                </p>
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0 h-8 w-8"
                  onClick={() => handleRemove(link)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        <div className="space-y-2">
          <p className="text-sm font-medium text-theme-1">Notes</p>
          <ul className="text-sm text-theme-2 list-disc list-inside space-y-1 pl-1">
            <li>To add multiple URLs, separate with a new line.</li>
            <li>
              Only the visible text on the website will be imported at this
              time.
            </li>
            <li>Paid articles are not supported.</li>
            <li>YouTube videos are not supported at this time.</li>
          </ul>
        </div>

        {/* Add button in bottom right */}
        <div className="flex justify-end">
          <Button
            onClick={handleConfirm}
            disabled={!selectedLinks.length}
            className="px-6 bg-brand-gradient text-theme-0 text-sm hover:brightness-90 transition disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:brightness-100"
          >
            Add links
          </Button>
        </div>
      </div>
    </>
  );
}
